import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import LoginModal from './LoginModal';

// Landing page: the "Urban Playbook. För ny hälsa och nya stadsmönster." site,
// served as a self-contained static document (public/playbook.html) and
// rendered full-viewport in an isolated iframe so its global CSS/fonts/script
// do not collide with the rest of the app (App.css).
//
// This page also owns the login popup (LoginModal). It opens two ways:
//   1. The iframe's login button postMessages the parent ('ut:open-login').
//   2. A `?login` query param on the URL. Everything that used to send the
//      user to the old standalone /login page now navigates here with
//      ?login=1 instead (the /login route redirect, ProtectedRoute, the
//      reset-password links, playbook.html's non-embedded fallback), so the
//      login experience is always the styled popup over the website — the
//      plain /login page is never shown.
// Auth still flows through AuthContext because the popup lives in React, not
// inside the static iframe document.
const HomePage = () => {
  const [loginLang, setLoginLang] = useState(null); // null = modal closed
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Open from ?login=1 (optionally &lang=en), then strip the params so a
  // refresh or closing the modal does not reopen it and the URL stays clean.
  // Keep the router state: it carries ProtectedRoute's `from`, which the
  // popup returns to after login.
  useEffect(() => {
    if (searchParams.has('login')) {
      setLoginLang(searchParams.get('lang') === 'en' ? 'en' : 'sv');
      const next = new URLSearchParams(searchParams);
      next.delete('login');
      next.delete('lang');
      setSearchParams(next, { replace: true, state: location.state });
    }
    // Read once from the initial URL on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open from the iframe's login button.
  useEffect(() => {
    const onMessage = (event) => {
      // playbook.html is served from this same origin; never trust a message
      // from anywhere else.
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'ut:open-login') return;
      // Follow the language the visitor picked on the landing page.
      setLoginLang(event.data.lang === 'en' ? 'en' : 'sv');
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <>
      <iframe
        title="Urban Playbook. För ny hälsa och nya stadsmönster."
        src="/playbook.html"
        // the landing page embeds a Vimeo player; fullscreen must be allowed at every frame level
        allow="fullscreen; picture-in-picture; autoplay; encrypted-media"
        allowFullScreen
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
      {loginLang && (
        <LoginModal lang={loginLang} onClose={() => setLoginLang(null)} />
      )}
    </>
  );
};

export default HomePage;
